import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Map as MapIcon, Route as RouteIcon } from "lucide-react";
import { fetchTrail } from "../api/trails";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import { AtlasMap } from "../components/map/AtlasMap";
import { MAP_COLORS } from "../components/map/map-colors";
import { LANDMARK_CATEGORIES } from "../lib/landmark-icons";
import { AtlasPanel } from "../components/AtlasPanel";
import { ProgressRing } from "../components/ProgressRing";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { GoalButton } from "../components/GoalButton";
import { AddToListButton } from "../components/AddToListButton";
import { StarRating } from "../components/StarRating";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import type { MapFeatureCollection, HeatmapFeatureCollection } from "../api/map";
import type { TrailResponse } from "@atlas/shared";
import { placeOf } from "../lib/place-names";

function computeBbox(fc: TrailResponse["geojson"]): [number, number, number, number] | null {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const f of fc.features) {
    for (const [lng, lat] of f.geometry.coordinates) {
      if (lng < w) w = lng;
      if (lng > e) e = lng;
      if (lat < s) s = lat;
      if (lat > n) n = lat;
    }
  }
  if (!isFinite(w)) return null;
  return [w, s, e, n];
}

function LegendSwatch({ color, dashed }: { color: string; dashed?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 22,
        height: 0,
        borderTop: `3px ${dashed ? "dashed" : "solid"} ${color}`,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
  );
}

function TrailMapLegend({ hasCoverage }: { hasCoverage: boolean }) {
  const rows: { swatch: React.ReactNode; label: string }[] = [
    { swatch: <LegendSwatch color={MAP_COLORS.gold} />, label: "Section collected" },
    ...(hasCoverage
      ? [{ swatch: <LegendSwatch color={MAP_COLORS.sky} />, label: "Walked by you" }]
      : []),
    { swatch: <LegendSwatch color={MAP_COLORS.grey} dashed />, label: "Not walked yet" },
  ];
  return (
    <div
      style={{
        position: "absolute",
        left: 12,
        bottom: 12,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "10px 14px",
        background: "color-mix(in srgb, var(--bg-app) 82%, transparent)",
        backdropFilter: "blur(6px)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        pointerEvents: "none",
      }}
    >
      {rows.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {r.swatch}
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
            {r.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// Spec 33: where the trail splits into official either/or roads, either
// one counts. Each split shows both roads side by side with their length and
// the viewer's walked %, and which one their trail % is following (the
// further along, spec 33 round 2). Optional loops are never needed. The
// every-route badge needs the whole trail finished AND both roads at every
// headline split (and each headline loop) walked. Copy mirrored in mobile
// app/trail/[id].tsx.
type Alternate = NonNullable<TrailResponse["alternates"]>[number];

const WALKED_PCT = 80; // TRAIL_COMPLETE_FRACTION: a road counts in full from here

function roadWalked(a: Alternate, road: "main" | "alternate"): boolean {
  return road === "main" ? (a.mainCoveragePct ?? 0) >= WALKED_PCT : a.credited;
}

function RoadCard({
  label,
  name,
  km,
  pct,
  walked,
  following,
  isAuthenticated,
}: {
  label: string;
  name: string;
  km: number | null | undefined;
  pct: number;
  walked: boolean;
  following: boolean;
  isAuthenticated: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${following ? "var(--border-strong, var(--border-default))" : "var(--border-subtle)"}`,
        borderRadius: "var(--radius-md)",
        padding: "12px 14px",
        background: "var(--surface-sunken)",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div className="eyebrow" style={{ fontSize: 10 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", minWidth: 0 }}>{name}</span>
        {km != null && (
          <span className="tnum" style={{ fontSize: 12.5, color: "var(--text-muted)", flexShrink: 0 }}>
            {km} km
          </span>
        )}
      </div>
      {isAuthenticated && (
        <>
          <div style={{ height: 5, borderRadius: 3, background: "var(--surface-raised)", overflow: "hidden" }}>
            <div
              style={{
                width: `${Math.min(100, pct)}%`,
                height: "100%",
                background: walked ? "var(--status-success)" : "var(--sky-400)",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span className="tnum" style={{ fontSize: 12, color: walked ? "var(--status-success)" : "var(--text-secondary)" }}>
              {walked ? "Walked" : pct > 0 ? `${pct}% walked` : "Not walked yet"}
            </span>
            {following && !walked && (
              <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Counting towards your %</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function badgeRule(splits: boolean, loops: boolean): string {
  const what =
    splits && loops
      ? "walk both roads at each split and each optional loop"
      : splits
        ? "walk both roads at each split"
        : "walk each optional loop";
  return `Finish the whole trail and ${what}. One road is enough to finish the trail; this badge is for walking them all.`;
}

function RouteChoices({
  alternates,
  badge,
  trailFinished,
  isAuthenticated,
}: {
  alternates: NonNullable<TrailResponse["alternates"]>;
  badge: { slug: string; name: string } | null | undefined;
  trailFinished: boolean;
  isAuthenticated: boolean;
}) {
  const splits = alternates.filter((a) => a.kind === "alternate");
  const loops = alternates.filter((a) => a.kind === "extra");
  const headline = alternates.filter((a) => a.headline);
  const headlineDone = headline.filter((a) => a.credited && (a.kind === "extra" || roadWalked(a, "main"))).length;
  const intro =
    splits.length > 0
      ? "Where the trail splits, either road counts. Your trail % follows whichever one you're further along, and a road counts in full once you've walked 80% of it."
      : "These loops are part of the trail but not needed to finish it.";

  return (
    <AtlasPanel title={splits.length > 0 ? "Route choices" : "Optional loops"}>
      <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.55 }}>{intro}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {splits.map((a) => {
          const mainPct = a.mainCoveragePct ?? 0;
          const altPct = a.coveragePct;
          const mainWalked = roadWalked(a, "main");
          const altWalked = roadWalked(a, "alternate");
          // The road the trail % is following: the further along (the main
          // line on a tie, which is also what an unwalked split shows).
          const followAlt = altPct > mainPct;
          return (
            <div key={a.id} style={{ minWidth: 0 }}>
              <div
                className="r-wrap"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}
              >
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{a.stretch}</span>
                {isAuthenticated && badge && a.headline && (
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {mainWalked && altWalked ? "Both roads walked" : `${[mainWalked, altWalked].filter(Boolean).length} of 2 roads walked`}
                  </span>
                )}
              </div>
              <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
                <RoadCard
                  label="Main route"
                  name={a.mainName}
                  km={a.mainDistanceKm}
                  pct={mainPct}
                  walked={mainWalked}
                  following={isAuthenticated && !followAlt && mainPct > 0}
                  isAuthenticated={isAuthenticated}
                />
                <RoadCard
                  label="Alternative"
                  name={a.name}
                  km={a.distanceKm}
                  pct={altPct}
                  walked={altWalked}
                  following={isAuthenticated && followAlt}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            </div>
          );
        })}
        {loops.length > 0 && (
          <div style={{ minWidth: 0 }}>
            {splits.length > 0 && (
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>
                Optional loops
              </div>
            )}
            <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
              {loops.map((a) => (
                <RoadCard
                  key={a.id}
                  label={a.stretch}
                  name={a.name}
                  km={a.distanceKm}
                  pct={a.coveragePct}
                  walked={a.credited}
                  following={false}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {badge && headline.length > 0 && (
        <div
          className="r-wrap"
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Badge variant="gold">{badge.name}</Badge>
          <span className="r-basis-full" style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
            {badgeRule(splits.some((a) => a.headline), loops.some((a) => a.headline))}
            {isAuthenticated &&
              ` ${trailFinished ? "Trail finished" : "Trail not finished yet"} · ${headlineDone} of ${headline.length} ${
                headline.length === 1 ? "choice" : "choices"
              } walked both ways.`}
          </span>
        </div>
      )}
    </AtlasPanel>
  );
}

function TrailContent({ data, isAuthenticated }: { data: TrailResponse; isAuthenticated: boolean }) {
  const navigate = useNavigate();
  const { trail, sections, geojson, rating } = data;
  const remaining = trail.totalSections - trail.completedSections;

  // Canonical always uses the slug, even when navigated to by uuid - the
  // API response carries the slug regardless of which form the :id param was.
  const canonicalPath = `/trails/${trail.slug}`;
  // Matches placeOf()/trailPage() in scripts/prerender.mjs (build-time
  // static pages) so the two SEO layers agree once client JS hydrates.
  const place = placeOf(trail.region, trail.country);
  usePageMeta({
    title: `${trail.name} - ${trail.distanceKm != null ? `${Math.round(trail.distanceKm)} km ` : ""}trail in ${place} | Atlas`,
    description: `${trail.name}: ${trail.distanceKm != null ? `${Math.round(trail.distanceKm)} km` : "a long-distance trail"} in ${place}, in ${trail.totalSections} sections. Track your progress on Atlas.`,
    canonicalPath,
  });

  // Three unambiguous colours on this map: gold = section collected, sky =
  // the parts you actually walked (coverage overlay), dashed grey = trail
  // not walked yet. "in_progress" deliberately maps to not_collected — the
  // sky overlay carries the partial-progress signal, so tinting the whole
  // section line blue would double-encode it and hide what's missing.
  const mapFeatures: MapFeatureCollection = React.useMemo(
    () => ({
      type: "FeatureCollection",
      features: [
        ...geojson.features.map((f) => ({
          type: "Feature" as const,
          geometry: f.geometry,
          properties: {
            id: f.properties.sectionId,
            name: trail.name,
            slug: trail.slug,
            type: "trail" as const,
            status: f.properties.status === "collected" ? ("collected" as const) : ("not_collected" as const),
          },
        })),
        // Spec 33: official alternative routes, gold once walked (they then
        // count for the stretch they stand in for).
        ...(data.alternatesGeojson?.features ?? []).map((f, i) => ({
          type: "Feature" as const,
          geometry: f.geometry,
          properties: {
            id: `${f.properties.alternateId}-${i}`,
            name: trail.name,
            slug: trail.slug,
            type: "trail" as const,
            status: f.properties.credited ? ("collected" as const) : ("not_collected" as const),
          },
        })),
      ],
    }),
    [geojson, data.alternatesGeojson, trail.name, trail.slug],
  );

  // The walked pieces of INCOMPLETE sections, drawn with the activity-trace
  // styling so an in-progress section shows WHICH parts are covered instead
  // of tinting the whole line. Collected sections are excluded — they render
  // as continuous gold, and drawing sky coverage on top of them muddied the
  // gold into a blue-gold mixture (ATL-49 round 3).
  const coverageOverlay: HeatmapFeatureCollection | null = React.useMemo(() => {
    if (!data.coverage || data.coverage.features.length === 0) return null;
    const collectedIds = new Set(sections.filter((s) => s.completed).map((s) => s.id));
    const features = data.coverage.features
      .filter((f) => !collectedIds.has(f.properties.sectionId))
      .map((f, i) => ({
        type: "Feature" as const,
        geometry: f.geometry,
        properties: { id: `${f.properties.sectionId}-${i}`, name: trail.name, startedAt: "" },
      }));
    return features.length > 0 ? { type: "FeatureCollection", features } : null;
  }, [data.coverage, sections, trail.name]);

  const bbox = React.useMemo(() => computeBbox(geojson), [geojson]);

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          name: trail.name,
          description: trail.description ?? undefined,
          url: `https://goatlas.co.uk${canonicalPath}`,
        }}
      />
    <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
      <button
        type="button"
        onClick={() => void navigate(isAuthenticated ? { to: "/map" } : { to: "/trails" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> {isAuthenticated ? "Map" : "All trails"}
      </button>

      {/* hero */}
      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
          flexShrink: 0,
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 28 }}>
          <ProgressRing value={trail.completionPct} max={100} size={132} stroke={12} label="Complete" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {[trail.country, trail.region].filter(Boolean).join(" · ") || "National trail"}
            </div>
            <h1
              style={{
                font: "var(--type-display)",
                fontSize: 44,
                color: "var(--text-primary)",
                margin: "0 0 12px",
                letterSpacing: "-.02em",
              }}
            >
              {trail.name}
            </h1>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {trail.distanceKm != null && <Badge variant="neutral">{trail.distanceKm} km</Badge>}
              <Badge variant="gold" dot>
                {trail.totalSections} sections · {trail.completedSections} done · {remaining} to go
              </Badge>
              {trail.everyRouteWalked && (
                <Badge variant="gold">{trail.everyRouteBadge?.name ?? "Every route walked"}</Badge>
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <StarRating itemType="trail" itemId={trail.id} initial={rating} isAuthenticated={isAuthenticated} />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
            {/* /map is a protected route: guests get the sign-up CTA as the
                one primary action instead (ATL-132). */}
            {isAuthenticated && (
              <Button
                variant="primary"
                leftIcon={<MapIcon size={16} />}
                onClick={() => void navigate({ to: "/map", search: { trail: trail.id } })}
              >
                View on map
              </Button>
            )}
            {isAuthenticated ? (
              <>
                <GoalButton targetType="trail" targetId={trail.id} />
                <AddToListButton itemType="trail" itemId={trail.id} />
              </>
            ) : (
              <Button variant="primary" onClick={() => void navigate({ to: "/signup" })}>
                Sign up to track progress
              </Button>
            )}
            <Button variant="ghost" leftIcon={<RouteIcon size={16} />} onClick={() => void navigate({ to: "/route-analyser" })}>
              Check a route against this
            </Button>
          </div>
        </div>
      </div>

      {/* inline map */}
      <div
        style={{
          position: "relative",
          height: "clamp(420px, 55vh, 680px)",
          flexShrink: 0,
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--ring-top)",
        }}
      >
        <AtlasMap
          features={mapFeatures}
          heatmap={coverageOverlay}
          mapTheme={{ traceColor: MAP_COLORS.sky }}
          traceAboveTrails
          selectedId={null}
          filter="all"
          landmarkCategories={LANDMARK_CATEGORIES}
          onFeatureSelect={() => {}}
          onBoundsChange={() => {}}
          fitBounds={bbox}
        />
        <TrailMapLegend hasCoverage={!!coverageOverlay} />
      </div>

      {data.alternates && data.alternates.length > 0 && (
        <RouteChoices
          alternates={data.alternates}
          badge={trail.everyRouteBadge}
          trailFinished={trail.totalSections > 0 && trail.completedSections >= trail.totalSections}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* section table */}
      <AtlasPanel title="Sections">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["#", "Name", "Distance", "Status", "Completed"].map((h) => (
                  <th
                    key={h}
                    className="eyebrow"
                    style={{
                      textAlign: "left",
                      padding: "8px 12px",
                      borderBottom: "1px solid var(--border-subtle)",
                      fontSize: 10,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <tr key={section.id}>
                  <td className="tnum" style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)", borderBottom: "1px solid var(--border-subtle)" }}>
                    {section.sectionNumber}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", borderBottom: "1px solid var(--border-subtle)" }}>
                    {section.name ?? `Section ${section.sectionNumber}`}
                    {section.viaAlternate && (
                      <div style={{ fontSize: 11.5, fontWeight: 400, color: "var(--text-muted)", marginTop: 2 }}>
                        Includes your walk on the {section.viaAlternate} route
                      </div>
                    )}
                  </td>
                  <td className="tnum" style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}>
                    {section.distanceKm != null ? `${section.distanceKm} km` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
                    {section.completed ? (
                      <Badge variant="success" dot>Collected</Badge>
                    ) : (section.coveragePct ?? 0) > 0 ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
                        <div style={{ flex: 1, height: 5, borderRadius: 3, background: "var(--surface-sunken)", overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${section.coveragePct}%`,
                              height: "100%",
                              borderRadius: 3,
                              background: "var(--sky-400)",
                            }}
                          />
                        </div>
                        <span className="tnum" style={{ fontSize: 11.5, color: "var(--sky-300)", flexShrink: 0 }}>
                          {section.coveragePct}%
                        </span>
                      </div>
                    ) : (
                      <Badge variant="neutral">Remaining</Badge>
                    )}
                  </td>
                  <td className="tnum" style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}>
                    {section.completedAt ? new Date(section.completedAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AtlasPanel>
    </div>
    </>
  );
}

export default function TrailDetailPage({ staticId, staticData }: { staticId?: string; staticData?: TrailResponse } = {}) {
  const params = useParams({ strict: false });
  const id = staticId ?? (params as { id?: string }).id ?? "";
  const { user: me } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["trails", id],
    queryFn: () => fetchTrail(id),
    enabled: !staticData && !!id,
    initialData: staticData,
  });

  if (isLoading || (!data && !staticData)) {
    return (
      <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="200px" />
        <Skel height="360px" />
        <Skel height="220px" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<MapIcon size={18} />} title="Trail not found" />
      </div>
    );
  }

  return <TrailContent data={data} isAuthenticated={!!me} />;
}
